import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AdminPagesService } from './adminPagesSlice'
import PageHeadSection from './components/PageHeadSection'
import BaseApp from '../BaseApp'
import { useDispatch } from 'react-redux'
import { addNewForm, createOrUpdateElementData } from '../../components/Form/formsSlice'
import SectionBoxContainer from '../../components/SectionBoxContainer'
import NotFound from '../../components/NotFound'
import GenerateForm from '../../components/Form/GenerateForm'
import FormActionButtonsSection from '../../components/Form/FormActionButtonsSection'
import { LoadingButton } from '@mui/lab'
import DynamicForm, { IDynamicForm, IFormElementsData, IRequiredField } from '../../components/Form/DynamicForm'
import { IAxiosDefaultDataObject } from '../../config'
import { Dispatch, UnknownAction } from '@reduxjs/toolkit'
import { handleRequestError } from '../../services/api'
import { AdminPagesTypes } from './Interfaces'
import ItemsApp from '../items/ItemsApp'
import { IModalObject } from '../../components/GridData'

interface IProps {
    updateItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => void
    customPageName?: string,
    customPageType?: AdminPagesTypes,
    hidePageHeadBox?: boolean,
    forceCreatePage?: boolean,
    afterSuccess?: () => void,
    noRedirect?: boolean,
    advancedAfterSuccess?: (item: IAxiosDefaultDataObject) => void,
    customForm?: IDynamicForm,
    addCustomContent?: (item: IAxiosDefaultDataObject) => JSX.Element,
    updateSubmitData?: (data: IAxiosDefaultDataObject) => IAxiosDefaultDataObject,
}

export default function AdminDynamicCreateOrUpdatePage(props: IProps) {

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const params = useParams()

    const { 
        updateItem, 
        customPageName, 
        customPageType, 
        hidePageHeadBox, 
        forceCreatePage, 
        afterSuccess, 
        noRedirect, 
        advancedAfterSuccess, 
        customForm,
        addCustomContent,
        updateSubmitData,
    } = props

    const [notFound, setNotFound] = useState<boolean>(false)

    const [isUpdate, setIsUpdate] = useState<boolean>(forceCreatePage ? false : params.id ? true  : false)
    const [itemId, setItemId] = useState<number>(params.id ? Number(params.id) : 0)

    const page = AdminPagesService.usePageDetails(customPageName ? customPageName : params.page_name ? params.page_name : "")

    const appClass = page.appClass ? page.appClass : new BaseApp()
    const form = customForm ? customForm : appClass.formConfig.form
    const currentForm = DynamicForm.useReturnSingleForm(form.name)

    const elementsData = DynamicForm.useReturnAllFormsData()
    const singleElemDataValue = (name: string) => DynamicForm.returnSingleFormElementData(name, elementsData).value

    const [itemDetails, setItemDetails] = useState<IModalObject>()


    /**
     * Create form elem data
     */
    const createFormElemData = () => {
        form.elements.map((elem) => dispatch(createOrUpdateElementData({
            name: elem.id,
            value: ""
        })))
    }

    /**
     * Reset form
     */
    const resetForm = () => {
        createFormElemData()
        DynamicForm.createOrUpdateFormMessage(form.name, { shown: false, type: "success", message: "" }, dispatch)
    }

    /**
     * Load item
     */
    const loadItem = () => {
        DynamicForm.updateFormLoadingStatus(form.name, true, dispatch)
        appClass.singleInstanceDetails(itemId ? itemId : 0)
            .then(response => {

                
                const item = response.data

                form.elements.map((elem) => dispatch(createOrUpdateElementData({
                    name: elem.id,
                    value: item[elem.id.replace(appClass.elemPrefix, "")]
                })))

                setNotFound(false)
                DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)

                setItemDetails(item)

            })
            .catch(error => {

                handleRequestError(error, dispatch)

                switch(error.response.status){

                    case 404:
                        setNotFound(true)

                }

                DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
            })
    }

    /**
     * Handle submit
     */
    const handleSubmit = () => {

        let data: IAxiosDefaultDataObject = {}

        /**
         * Fill data
         */
        form.elements.map((elem) => {
            let dataName = elem.id.replace(appClass.elemPrefix, "")
            data[dataName] = singleElemDataValue(elem.id)
        })

        let requiredFields: Array<IRequiredField> = appClass.formConfig.requiredFields

        if (isUpdate){
            data.id = itemId
            data.update = true
        }

        if (updateSubmitData){
            data = updateSubmitData(data)
        }

        DynamicForm.resetRequiredFieldsHelperText(requiredFields, elementsData, dispatch)
        const verifyForm = DynamicForm.verifyForm(elementsData, requiredFields)

        if (verifyForm.isValid){
            DynamicForm.updateFormLoadingStatus(form.name, true, dispatch)

            appClass.createNewInstance(data)
                .then(response => {

                    const { data } = response
                    const message = data.message ? data.message : "Success!"

                    DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "success", message: message }, dispatch)

                    updateItem(data, dispatch)

                    if (advancedAfterSuccess){
                        advancedAfterSuccess(data)
                    }

                    resetForm()

                    if (!noRedirect){
                        navigate(`/panel/${page.name}`)
                    }

                    if (afterSuccess){
                        afterSuccess()
                    }

                })
                .catch(error => {

                    console.log(error)
                    const message = error.response.data.message ? error.response.data.message : "Fatal Error"

                    DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "error", message: message }, dispatch)

                })
        }else{
            DynamicForm.updateRequiredFieldsHelperText(verifyForm.fields, elementsData, dispatch)
        }

    }

    /**
     * Create Form
     */
    useEffect(() => {

        dispatch(addNewForm({
            name: form.name,
            elements: form.elements
        }))

        createFormElemData()

    }, [customForm])

    /**
     * Load item
     */
    /**
     * Load location
     */
    useEffect(() => {

        if (itemId){
            setIsUpdate(true)
        }

        if (isUpdate && itemId){
            loadItem()
        }else{
            setIsUpdate(false)
            resetForm()
        }

        if (forceCreatePage){
            setIsUpdate(false)
        }
    }, [itemId])

    return(
        <div>
            {!hidePageHeadBox && <PageHeadSection customPageType={customPageType} pageName={page.name} />}

            <SectionBoxContainer
                showTitle={appClass.formConfig.sectionBox.showTitle}
                title={appClass.formConfig.sectionBox.title}
                description={appClass.formConfig.sectionBox.description}
            >
                {notFound ? <NotFound /> : (
                    <>
                        <GenerateForm name={form.name} />
                        {addCustomContent && addCustomContent(itemDetails ? itemDetails : {})}
                    </>
                )}

            </SectionBoxContainer>

            <FormActionButtonsSection>
                <LoadingButton disabled={notFound} loading={currentForm.loading} className="general-button submit-style" variant="contained" onClick={() => handleSubmit()} >{isUpdate ? page.messages.editShort : page.messages.createShort}</LoadingButton>
            </FormActionButtonsSection>
        </div>
    )
}